/**
 * voice-v8 — lange Diktate in Teilen. (29.09.2026, Ticket 2618)
 *
 * ## Der Anlass
 *
 * Albert diktiert im Auto über die Sprechtaste, oft länger als eine
 * Viertelstunde. Die Höchstdauer schnitt ihn ab: am 25.09. dreimal bei
 * 900 s, am 28.09. um 13:25 (nach der Anhebung, pre.3308) bei 1.527 s —
 * `endstille=0,0`, er sprach bis zum Schnitt und darüber hinaus.
 *
 * ## Warum Teile statt einer höheren Grenze
 *
 * Belegt am 29.09.:
 *   - nginx nimmt je Anfrage höchstens 20 MB an (`client_max_body_size 20M`
 *     für voice.vespera.at, gespiegelt als SERVER_ANNAHME_MB). Die Aufnahme
 *     vom 28.09. hatte 15,9 MB für 25 min; mit der am 25.09. gemessenen
 *     Rate (11,1 MB je 15 min) sind es 18,1 MiB — viel mehr passt nicht.
 *   - Hinter nginx lässt multer 25 MB zu (MAX_AUDIO_BYTES), Groq Whisper
 *     nimmt je Datei 25 MB.
 *   - Und die Zeit: eine Anfrage über 91 min Ton lief am 17.09. bei Groq in
 *     „Request timed out" (server/transcribe-file.ts zerlegt darum in
 *     10-min-Stücke).
 * Eine höhere Grenze bräuchte also nginx, multer UND ein Zerlegen auf dem
 * Server. Teile brauchen nichts davon: jeder bleibt so groß wie heute.
 *
 * ## Der Weg
 *
 * An der Grenze beginnt die Sprechtaste auf DERSELBEN Leitung einen neuen
 * Rekorder, bevor der alte endet (ptt.ts, `wechsleTeil`). Der fertige Teil
 * geht sofort an /api/voice/erkennen — dieselbe Route wie das Senden, ohne
 * Modellzug —, während weiter aufgenommen wird. Mit `teil=1` legt der
 * Server seinen Ton ab wie beim Senden (`tonUrl`), auch wenn das Konto
 * gesperrt ist oder er nichts darin hört. Am Ende trägt die letzte Aufnahme
 * die früheren (`vorlaufVon`), und der EINE Hochladeweg (streamPtt) schickt
 * deren Text als `vorText` und ihre Tondateien als `vorTon` mit: der Server
 * stellt den Text vor den Text der letzten Aufnahme und hängt alle
 * Tondateien an die Nachricht — EINE Nachricht, EIN Zug.
 *
 * ⚠️ Weil die Teile an der Aufnahme selbst hängen, gilt das für JEDES Ende
 *    ohne eigenen Code: Senden, „Erneut senden", ins Textfeld, Umschreiben,
 *    Verwerfen mit Zurückholen. Nur „Verwerfen" (Knopf) verwirft wirklich.
 *
 * ## Nachbesserung 29.09.2026 (Prüfbefund): kein Teil fällt still weg
 *
 * Bis zur Nachbesserung galt „gesperrt" und „nichts" als endgültig: der
 * Teil wurde übersprungen, sein Ton beim nächsten Teil vergessen — und am
 * Server lag er nirgends. Ein Konto, das um 24 min gesperrt war und beim
 * Senden längst wieder frei, verlor so 24 min. Jetzt vergisst die Seite
 * einen Ton nur, wenn der Teil GETRAGEN ist (`getragen`): sein Text ist da,
 * oder der Server hat den Ton abgelegt und nichts darin gehört. Alles
 * andere wird beim Senden noch einmal erkannt; ein Teil ohne Text geht nur
 * weiter, wenn seine Tondatei am Server liegt.
 */

import { verbindeTeile } from "../../shared/diktat-teile.js";
import type { GanzErkannt } from "./ganz-erkennen.js";

/**
 * Erkennt einen fertigen Teil — in der App erkenneGanzeAufnahme ohne Endwort.
 * `ablegen`: der Server soll den Ton ablegen (`teil=1`) — nur, solange er
 * ihn noch nicht hat; sonst läge er zweimal da.
 */
export type TeilErkenner = (ton: Blob, mime: string, ablegen: boolean) => Promise<GanzErkannt>;

/**
 * Kleiner ist die letzte Aufnahme kein Ende, das trägt. Dieselbe Schwelle
 * wie „Zu kurz" beim Weg ins Textfeld (app.ts, pttBeendeInsFeld) — sie ist
 * die höhere der beiden; unter ihr (und unter LEERE_AUFNAHME_BYTES) würde
 * die Aufnahme samt den früheren Teilen verworfen. Ein früherer Teil
 * darunter trägt ebenso keinen Ton (ein paar Zehntelsekunden, kaum mehr
 * als der Dateikopf) und wird gar nicht erst aufgenommen.
 */
export const KLEINSTER_TEIL_BYTES = 1200;

interface Teil {
  /** Der Ton — bis der Teil getragen ist; der jüngste behält ihn immer. */
  ton: Blob | null;
  mime: string;
  erkannt: Promise<GanzErkannt>;
  ergebnis: GanzErkannt | null;
  /** Wo der Server den Ton abgelegt hat (`/uploads/ptt-…`) — null, solange nirgends. */
  tonUrl: string | null;
}

/**
 * Ist der Teil sicher, ohne dass die Seite seinen Ton noch hält? Sein Text
 * ist da — oder der Server hat den Ton abgelegt und nichts darin gehört
 * (dann bleibt die Datei an der Nachricht, siehe `sammle`).
 *
 * ⚠️ „gesperrt" zählt nie dazu: das Konto kann beim Senden wieder frei
 *    sein, dann wird der Teil noch einmal erkannt (Prüfbefund 29.09.).
 */
function getragen(t: Teil): boolean {
  const e = t.ergebnis;
  if (!e) return false;
  return e.ok || (e.grund === "nichts" && t.tonUrl !== null);
}

/**
 * Ein früherer Teil ist auch im zweiten Anlauf ohne Text, und der Server
 * hat seinen Ton nicht (oder die Erkennung scheiterte an Netz/Server).
 *
 * ⚠️ Die Meldung trägt den Grund NICHT: app.ts liest „leer" in einer
 *    Fehlermeldung als „nichts gehört" und böte dann kein „Erneut senden"
 *    an — genau das darf hier nicht geschehen. Der Grund steht in `grund`.
 */
export class TeilNichtErkannt extends Error {
  constructor(public readonly grund: string) {
    super("teil-nicht-erkannt");
  }
}

const VORLAEUFE = new WeakMap<Blob, DiktatVorlauf>();

/** Die früheren Teile, die diese (letzte) Aufnahme trägt — oder null. */
export function vorlaufVon(ton: Blob): DiktatVorlauf | null {
  return VORLAEUFE.get(ton) ?? null;
}

/** Was die früheren Teile zur Sendung beitragen. */
export interface Vorlauf {
  /** Ihr Text, der Reihe nach (verbindeTeile). */
  text: string;
  /** Ihre Tondateien am Server, der Reihe nach. */
  ton: string[];
}

export class DiktatVorlauf {
  private readonly teile: Teil[] = [];

  constructor(private readonly erkenne: TeilErkenner) {}

  /** Wie viele frühere Teile es gibt. */
  get anzahl(): number {
    return this.teile.length;
  }

  /** Ein fertiger Teil — seine Erkennung (samt Ablegen am Server) beginnt sofort. */
  nimm(ton: Blob, mime: string): void {
    if (ton.size < KLEINSTER_TEIL_BYTES) return;
    // Getragene ältere Teile brauchen ihren Ton nicht mehr — so liegt im
    // Regelfall nie mehr als ein ganzer Teil zusätzlich im Speicher.
    for (const t of this.teile) {
      if (getragen(t)) t.ton = null;
    }
    const teil: Teil = {
      ton,
      mime,
      tonUrl: null,
      ergebnis: null,
      erkannt: Promise.resolve({ ok: false, grund: "netz" }),
    };
    teil.erkannt = this.starte(teil, ton);
    this.teile.push(teil);
  }

  private starte(t: Teil, ton: Blob): Promise<GanzErkannt> {
    return this.erkenne(ton, t.mime, t.tonUrl === null)
      .catch((): GanzErkannt => ({ ok: false, grund: "netz" }))
      .then((e) => {
        t.ergebnis = e;
        t.tonUrl = e.tonUrl ?? t.tonUrl;
        return e;
      });
  }

  /**
   * Text und Tondateien aller früheren Teile, der Reihe nach. Ein Teil, der
   * nicht getragen ist, wird jetzt noch einmal erkannt. Ohne Text geht er
   * nur weiter, wenn der Server seinen Ton hat und darin nichts hörte oder
   * das Konto gesperrt ist — dann trägt die Nachricht seine Tondatei (und
   * gesperrt sichert der Server die ganze Sendung, voice-command.ts). Sonst
   * wirft es: die Sendung gilt als nicht übertragen und kommt mit „Erneut
   * senden" wieder — der Ton liegt noch da, nichts fällt weg.
   */
  async sammle(): Promise<Vorlauf> {
    const texte: string[] = [];
    for (const t of this.teile) {
      let e = await t.erkannt;
      if (!getragen(t) && t.ton) {
        t.erkannt = this.starte(t, t.ton);
        e = await t.erkannt;
      }
      if (e.ok) {
        texte.push(e.text);
        continue;
      }
      if (t.tonUrl !== null && (e.grund === "nichts" || e.grund === "gesperrt")) continue;
      throw new TeilNichtErkannt(e.grund);
    }
    return {
      text: verbindeTeile(...texte),
      ton: this.teile.flatMap((t) => (t.tonUrl ? [t.tonUrl] : [])),
    };
  }

  /**
   * Das Ende des Diktats: die letzte Aufnahme trägt die früheren Teile.
   *
   * ⚠️ Ist sie (fast) leer — losgelassen gleich nach dem Wechsel —, wird
   *    der jüngste frühere Teil das Ende, und der Server erkennt ihn beim
   *    Senden noch einmal. Sonst fiele das ganze Diktat durch die Wege, die
   *    eine leere Aufnahme verwerfen („Zu kurz", „nichts gehört").
   */
  abschluss(letzter: Blob | null): Blob | null {
    let ende = letzter;
    if (!ende || ende.size < KLEINSTER_TEIL_BYTES) {
      const juengster = this.teile.pop();
      if (juengster?.ton) ende = juengster.ton;
      else if (juengster) this.teile.push(juengster);
    }
    if (ende && this.teile.length > 0) VORLAEUFE.set(ende, this);
    return ende;
  }
}
